import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, FileText, Layers, AlertTriangle, CheckCircle, Database } from "lucide-react";
import { PROJECTS, SPECIALIST_LANES } from "../config/wiseboardConfig";

interface SynthesisWorkspaceProps {
  caseId: string;
  dbProject: any;
  artifacts: any[];
  onArtifactSaved: () => void;
}

interface UpstreamBlock {
  projectType: string;
  projectName: string;
  content: string;
  version: number;
  confidence: number | null;
  status: string;
}

export default function SynthesisWorkspace({ caseId, dbProject, artifacts, onArtifactSaved }: SynthesisWorkspaceProps) {
  const [upstreamBlocks, setUpstreamBlocks] = useState<UpstreamBlock[]>([]);
  const [evidenceItems, setEvidenceItems] = useState<any[]>([]);
  const [artifactContent, setArtifactContent] = useState(artifacts[0]?.content || "");
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState<{ role: string; content: string }[]>([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingContext, setLoadingContext] = useState(true);

  // Load all upstream approved artifacts + evidence matrix
  useEffect(() => {
    const loadContext = async () => {
      setLoadingContext(true);

      // Get all projects for this case
      const { data: projects } = await supabase
        .from("wiseboard_projects")
        .select("*")
        .eq("case_id", caseId);

      if (!projects) { setLoadingContext(false); return; }

      // Upstream sources: P1, P1B, P2_PASS1, P2_PASS2, and selected specialist lanes
      const upstreamTypes = ["P1", "P1B", "P2_PASS1", "P2_PASS2", ...SPECIALIST_LANES];
      const approvedProjects = projects.filter(
        p => upstreamTypes.includes(p.project_type) && p.status === "approved"
      );

      // Fetch latest approved artifact for each upstream project
      const blocks: UpstreamBlock[] = [];
      for (const proj of approvedProjects) {
        const { data: arts } = await supabase
          .from("wiseboard_artifacts")
          .select("*")
          .eq("project_id", proj.id)
          .eq("approval_status", "approved")
          .order("version", { ascending: false })
          .limit(1);

        if (arts && arts.length > 0) {
          const art = arts[0];
          const projDef = PROJECTS[proj.project_type];
          blocks.push({
            projectType: proj.project_type,
            projectName: projDef?.name || proj.project_type,
            content: art.content || "",
            version: art.version,
            confidence: art.confidence,
            status: proj.status,
          });
        }
      }
      setUpstreamBlocks(blocks);

      // Load evidence matrix
      const { data: evidence } = await supabase
        .from("wiseboard_evidence_matrix")
        .select("*")
        .eq("case_id", caseId)
        .order("hypothesis_id", { ascending: true });
      setEvidenceItems(evidence || []);

      setLoadingContext(false);
    };
    loadContext();
  }, [caseId]);

  const buildSystemPrompt = () => {
    const project = PROJECTS["P20"];
    let prompt = project.systemPrompt + "\n\n";

    prompt += "## UPSTREAM CONTEXT (Auto-injected approved artifacts)\n\n";

    if (upstreamBlocks.length === 0) {
      prompt += "⚠️ No approved upstream artifacts found. You may need to wait for upstream projects to complete.\n\n";
    } else {
      for (const block of upstreamBlocks) {
        prompt += `### ${block.projectType} — ${block.projectName} (v${block.version}, confidence: ${block.confidence ?? "N/A"})\n`;
        prompt += block.content + "\n\n---\n\n";
      }
    }

    prompt += "## EVIDENCE MATRIX SNAPSHOT\n\n";
    if (evidenceItems.length === 0) {
      prompt += "No evidence items found.\n";
    } else {
      prompt += "| Hypothesis | Claim | Status | Confidence | Source | Pass |\n";
      prompt += "|---|---|---|---|---|---|\n";
      for (const e of evidenceItems) {
        prompt += `| ${e.hypothesis_id} | ${e.claim} | ${e.status} | ${e.confidence ?? "—"}% | ${e.source || "—"} | ${e.pass_number} |\n`;
      }
    }

    return prompt;
  };

  const saveArtifact = async () => {
    if (!dbProject) return;
    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { toast.error("Not authenticated"); setSaving(false); return; }

    const latestVersion = artifacts.length > 0 ? Math.max(...artifacts.map(a => a.version)) : 0;
    const { data, error } = await supabase.from("wiseboard_artifacts").insert({
      project_id: dbProject.id,
      user_id: user.id,
      name: "P20 — Synthesis",
      version: latestVersion + 1,
      content: artifactContent,
      approval_status: "draft",
    }).select().single();

    if (!error && data) {
      toast.success(`Synthesis artifact v${data.version} saved`);
      onArtifactSaved();
    }
    setSaving(false);
  };

  const sendToAI = async () => {
    if (!chatInput.trim()) return;
    setAiLoading(true);
    const newMessages = [...chatMessages, { role: "user", content: chatInput }];
    setChatMessages(newMessages);
    setChatInput("");

    try {
      const { data, error } = await supabase.functions.invoke("chat", {
        body: {
          messages: newMessages,
          model: "google/gemini-2.5-pro",
          systemPrompt: buildSystemPrompt(),
        },
      });
      if (error) throw error;
      const reply = data?.choices?.[0]?.message?.content || "No response received.";
      setChatMessages([...newMessages, { role: "assistant", content: reply }]);
    } catch (err: any) {
      toast.error("AI error: " + (err.message || "Unknown"));
    }
    setAiLoading(false);
  };

  const evidenceStats = {
    total: evidenceItems.length,
    found: evidenceItems.filter(e => e.status === "DATA_FOUND").length,
    notFound: evidenceItems.filter(e => e.status === "NOT_FOUND").length,
    stale: evidenceItems.filter(e => e.status === "POTENTIALLY_STALE").length,
    assumptions: evidenceItems.filter(e => e.status === "ASSUMPTION").length,
  };

  return (
    <div className="space-y-4">
      {/* Context Summary */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <Layers className="h-4 w-4" />
              Injected Upstream Context
            </CardTitle>
            {loadingContext && <span className="text-xs text-muted-foreground animate-pulse">Loading...</span>}
          </div>
          <CardDescription className="text-xs">
            These approved artifacts are auto-injected into the AI system prompt for synthesis
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {!loadingContext && upstreamBlocks.length === 0 && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4" />
              No approved upstream artifacts found. Approve upstream projects first.
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            {upstreamBlocks.map(b => (
              <Badge key={b.projectType} variant="outline" className="text-[10px] gap-1">
                <CheckCircle className="h-3 w-3 text-green-500" />
                {b.projectType} — {b.projectName} (v{b.version})
              </Badge>
            ))}
          </div>

          {/* Evidence stats */}
          <div className="flex items-center gap-3 text-xs border-t pt-2">
            <Database className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-muted-foreground">Evidence Matrix:</span>
            <Badge variant="outline" className="text-[10px] bg-green-50">{evidenceStats.found} found</Badge>
            <Badge variant="outline" className="text-[10px] bg-red-50">{evidenceStats.notFound} not found</Badge>
            <Badge variant="outline" className="text-[10px] bg-yellow-50">{evidenceStats.stale} stale</Badge>
            <Badge variant="outline" className="text-[10px] bg-gray-50">{evidenceStats.assumptions} assumptions</Badge>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Artifact Editor */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">Synthesis Output</CardTitle>
              <Button size="sm" variant="outline" onClick={saveArtifact} disabled={saving}>
                <FileText className="h-3.5 w-3.5 mr-1" />{saving ? "Saving..." : "Save"}
              </Button>
            </div>
            <CardDescription className="text-xs">Weighted Synthesis + Quantified Scorecard</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={artifactContent}
              onChange={e => setArtifactContent(e.target.value)}
              placeholder="Write or paste the weighted synthesis here..."
              rows={16}
              className="text-sm font-mono"
            />
            {artifacts.length > 0 && (
              <div className="mt-2 flex gap-2 flex-wrap">
                {artifacts.map(a => (
                  <Badge key={a.id} variant="outline" className="text-[10px] cursor-pointer" onClick={() => setArtifactContent(a.content || "")}>
                    v{a.version} — {a.approval_status}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* AI Chat with injected context */}
        <Card className="flex flex-col max-h-[calc(100vh-16rem)]">
          <CardHeader className="pb-2 shrink-0">
            <CardTitle className="text-sm">AI Synthesiser</CardTitle>
            <CardDescription className="text-xs">
              {upstreamBlocks.length} upstream blocks + {evidenceItems.length} evidence items injected
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-auto space-y-3 mb-3 min-h-[200px]">
              {chatMessages.length === 0 && (
                <div className="text-xs text-muted-foreground italic p-4 text-center">
                  {PROJECTS["P20"].helpPrompt}
                </div>
              )}
              {chatMessages.map((msg, i) => (
                <div key={i} className={`text-sm rounded-lg p-3 ${msg.role === "user" ? "bg-primary/10 ml-8" : "bg-muted mr-8"}`}>
                  <div className="font-medium text-xs mb-1 text-muted-foreground">{msg.role === "user" ? "You" : "AI"}</div>
                  <div className="whitespace-pre-wrap text-xs">{msg.content}</div>
                </div>
              ))}
              {aiLoading && <div className="text-xs text-muted-foreground animate-pulse">AI is synthesising...</div>}
            </div>
            <div className="flex gap-2 shrink-0">
              <Textarea
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                placeholder="Ask the synthesiser..."
                rows={2}
                className="text-sm"
                onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendToAI(); } }}
              />
              <Button size="icon" onClick={sendToAI} disabled={aiLoading || !chatInput.trim()}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
